<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { writable, get } from 'svelte/store';
  import { selectedStudy, selectedSite } from '$stores/selectionStore';
  import { user, userRole, userRoles, userReadOnly, isReadOnly } from '$stores/sessionStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { isFeatureEnabled, studyProperties as studyPropertiesStore } from '$stores/configStore';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  const dispatch = createEventDispatcher();
  export let showForm = false;
  export let editPerson = null;

  function hasAdminRole() {
    return hasAccessUiAction(
      'participants.personForm.adminCapabilities',
      get(userRoles),
      get(studyPropertiesStore),
      get(userReadOnly)
    );
  }

  let statusOptions = [];
  let timezoneOptions = [];
  let siteOptions = [];
  let languageOptions = [];
  let studyLanguageGUIDs = [];
  let studyLanguages = [];
  let studyProperties = [];
  let idMasks = {
    siteId: '',
    participantId: '{siteId}{###}',
    autoGenerateParticipantId: true,
    showGenerateParticipantIdButton: true,
  };
  let nextSequentialId = 1;

  // Store original data for change detection
  let originalPersonData = null;
  let hasChanges = false;
  let isSettingUp = false;
  
  // Explicit flags to prevent race conditions in ID generation
  let formMode = 'create'; // 'create' or 'edit'
  let hasGeneratedIdThisSession = false;

  function getDefaultStatusGUID() {
    const defaultStatus = statusOptions.find((status) => status.default === true);
    return defaultStatus?.id || '';
  }

  function getEnglishLanguageGUID() {
    // Find English in the study languages
    const englishStudyLanguage = studyLanguages.find((sl) => sl.code === 'en_US');
    if (englishStudyLanguage) {
      return englishStudyLanguage.languageGUID;
    }
    
    // Fallback to all languages if not found in study languages
    const englishLanguage = languageOptions.find((lang) => lang.code === 'en_US');
    return englishLanguage?._id || '';
  }

  function shouldAutoGenerateSubjectId() {
    return idMasks.autoGenerateParticipantId !== false;
  }

  function shouldShowGenerateSubjectIdButton() {
    return idMasks.showGenerateParticipantIdButton !== false;
  }

  let personData = writable({
    _id: null,
    studyGUID: $selectedStudy?._id || '',
    subjectId: '',
    siteGUID: $selectedSite?._id || '',
    externalId: '',
    timezoneGUID: '',
    source: 'SUMMA',
    active: true,
    statusGUID: '',
    statusDate: new Date().toISOString().split('T')[0],
    statusChangeReason: '',
    statusChangeDate: '',
    notes: '',
    properties: {},
    subjectTemplateGUID: '',
    password: '',
    enrollmentDate: new Date().toISOString().split('T')[0],
    randomizationDate: '',
    blinded: true,
    languageGUID: '',
  });

  let subjectIdError = '';
  let passwordError = '';
  let timezoneError = '';
  let languageError = '';
  let statusChangeReasonError = '';
  let statusChangeDateError = '';
  let randomizationDateError = '';
  let originalStatusGUID = ''; // Track the original status when editing
  let lastStatusGUID = ''; // Track the last status to prevent continuous clearing

  function clearValidationErrors() {
    subjectIdError = '';
    passwordError = '';
    timezoneError = '';
    languageError = '';
    statusChangeReasonError = '';
    statusChangeDateError = '';
    randomizationDateError = '';
  }

  function clearSubjectIdError() {
    subjectIdError = '';
  }

  async function fetchStudyLanguages() {
    // Ensure we have a selected study
    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn('No study selected, cannot fetch study languages');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}`);
      const data = await response.json();
      // Store the full study language objects for default language lookup
      studyLanguages = Array.isArray(data) ? data : [];
      // Extract just the languageGUIDs for filtering
      studyLanguageGUIDs = studyLanguages.map(sl => sl.languageGUID);
      
      // Ensure defaults are set after study languages load
      ensureDefaultsSet();
    } catch (error) {
      console.error('Error fetching study languages:', error);
      studyLanguages = [];
      studyLanguageGUIDs = [];
    }
  }

  async function fetchStudyProperties() {
    // Ensure we have a selected study
    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn('No study selected, cannot fetch study properties');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/studyProperties/${$selectedStudy._id}`);
      const data = await response.json();

      if (data.data) {
        studyProperties = data.data.properties;

        // Get ID masks if available
        if (data.data.idMasks) {
          idMasks = {
            siteId: '',
            participantId: '{siteId}{###}',
            autoGenerateParticipantId: true,
            showGenerateParticipantIdButton: true,
            ...data.data.idMasks,
          };
        }

        if (formMode === 'create' && $selectedSite && $selectedSite._id && shouldAutoGenerateSubjectId()) {
          generateSubjectId();
        }
        
        // Ensure defaults are set after study properties load
        ensureDefaultsSet();
      }
    } catch (error) {
      console.error('Error fetching study properties:', error);
    }
  }

  function initializeDefaultProperties() {
    // SPTL-495: dropped `!isSettingUp` guard. The reactive setup block sets
    // isSettingUp=true then calls resetPersonData() → initializeDefaultProperties()
    // → guarded out. Study-properties fetch completes as a microtask BEFORE
    // the setTimeout(0) that clears isSettingUp, so ensureDefaultsSet()'s call
    // ALSO guards out. Defaults never applied. The per-property idempotency
    // check (`if (props[prop.key] !== undefined) return`) already prevents
    // overwriting anything a user or edit flow set, so the outer guard was
    // unnecessary belt-and-suspenders that broke the timing.
    if (studyProperties.length > 0) {
      personData.update((data) => {
        const props = { ...data.properties };
        studyProperties.forEach((prop) => {
          if (props[prop.key] !== undefined) return; // user/edit value already set — leave it
          if (prop.defaultValue !== null && prop.defaultValue !== undefined) {
            props[prop.key] = prop.defaultValue;
          } else if (prop.type === 'select' || prop.type === 'boolean') {
            // SPTL-488 parity: bind select/boolean to '' so the placeholder
            // option (`<option value="">Select ...`) is selected by default.
            // Without this, NG bound to undefined → no option matched → blank.
            props[prop.key] = '';
          }
        });
        return { ...data, properties: props };
      });
    }
  }

  // Normalize various ObjectId shapes to a plain hex string
  function asIdString(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      // Mongo Extended JSON shape
      if (value.$oid && typeof value.$oid === 'string') return value.$oid;
      // Some drivers attach an _id with $oid nested
      if (value._id && typeof value._id === 'object' && typeof value._id.$oid === 'string') return value._id.$oid;
      // Fallback to toString if it yields a 24-char hex
      try {
        const str = value.toString?.();
        if (typeof str === 'string' && /^[a-f0-9]{24}$/i.test(str)) return str;
      } catch {}
    }
    return '';
  }

  // Parse various date shapes and return YYYY-MM-DD string for <input type="date">
  function formatDateForInput(value) {
    if (!value) return '';
    let dateObj = null;
    if (value instanceof Date) {
      dateObj = value;
    } else if (typeof value === 'string') {
      dateObj = new Date(value);
    } else if (typeof value === 'object') {
      if (value.$date) {
        dateObj = new Date(value.$date);
      } else if (value.toDate instanceof Function) {
        try { dateObj = value.toDate(); } catch { /* ignore */ }
      }
    }
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    // Normalize to UTC date-only to avoid TZ shifts; yields YYYY-MM-DD
    return new Date(Date.UTC(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate()))
      .toISOString()
      .slice(0, 10);
  }

  // Function to ensure all defaults are set after async data loads
  function ensureDefaultsSet() {
    if (!editPerson && !isSettingUp) {
      // Set timezone if not set and we have the data
      if (!$personData.timezoneGUID && timezoneOptions.length > 0 && $selectedSite?.timezoneCode) {
        const siteTimezone = timezoneOptions.find((tz) => tz.code === $selectedSite.timezoneCode);
        if (siteTimezone) {
          $personData.timezoneGUID = siteTimezone.id;
        }
      }

      // Set language if not set and we have the data
      if (!$personData.languageGUID) {
        if (studyLanguageGUIDs.length > 0 && studyLanguages.length > 0) {
          const defaultStudyLanguage = studyLanguages.find((sl) => sl.languageDefault);
          if (defaultStudyLanguage) {
            $personData.languageGUID = defaultStudyLanguage.languageGUID;
          } else {
            const englishStudyLanguage = studyLanguages.find((sl) => sl.code === 'en_US');
            if (englishStudyLanguage) {
              $personData.languageGUID = englishStudyLanguage.languageGUID;
            }
          }
        } else if (languageOptions.length > 0) {
          const englishLanguage = languageOptions.find((lang) => lang.code === 'en_US');
          if (englishLanguage) {
            $personData.languageGUID = englishLanguage._id;
          }
        }
      }

    // Set randomization date if not set and feature is enabled
    if (!$personData.randomizationDate && isFeatureEnabled('randomization.enabled') && $personData.enrollmentDate) {
      const calculatedDate = calculateRandomizationDate();
      if (calculatedDate) {
        $personData.randomizationDate = calculatedDate;
      }
    }

    // Initialize default property values
    initializeDefaultProperties();
  }
}

  function buildMaskedIdCaptureRegex(mask, siteId) {
    if (!mask) return null;
    const escapedSiteId = siteId ? siteId.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') : '';
    let regex = mask.replace(/{siteId}/g, escapedSiteId);
    let captureAdded = false;
    regex = regex.replace(/{(#+)}/g, (_, hashes) => {
      if (!captureAdded) {
        captureAdded = true;
        return `(\\d{${hashes.length}})`;
      }
      return `\\d{${hashes.length}}`;
    });
    return captureAdded ? `^${regex}$` : null;
  }

  async function generateSubjectId() {
    // Explicit guards to prevent generating ID for existing participants
    if (formMode === 'edit') {
      console.log('Skipping ID generation: in edit mode');
      return;
    }
    
    if (hasGeneratedIdThisSession) {
      console.log('Skipping ID generation: already generated for this session');
      return;
    }
    
    // Ensure we have selected study and site
    if (!$selectedStudy || !$selectedStudy._id || !$selectedSite || !$selectedSite._id) {
      console.warn('Missing study or site information, cannot generate subject ID');
      return;
    }

    try {
      // Get the next available sequential ID number
      const response = await fetch(
        `${API_BASE_URL}/persons?studyGUID=${$selectedStudy._id}&page=1&pageSize=1000&siteGUID=${$selectedSite._id}`
      );
      const data = await response.json();
      
      // Check again after async operation - editPerson might have been set during fetch
      if (formMode === 'edit' || editPerson) {
        console.log('Aborting ID generation: switched to edit mode during fetch');
        return;
      }

      // Find highest existing sequential number for this site using the ID mask
      if (data.data && data.data.length > 0) {
        nextSequentialId = 1;
        const captureRegexString = buildMaskedIdCaptureRegex(
          idMasks.participantId,
          $selectedSite.siteId
        );
        const captureRegex = captureRegexString ? new RegExp(captureRegexString) : null;

        data.data.forEach((person) => {
          const subjectId = person.subjectId || '';
          if (!captureRegex) {
            return;
          }
          const match = subjectId.match(captureRegex);
          if (match && match[1]) {
            const num = parseInt(match[1], 10);
            if (!isNaN(num) && num >= nextSequentialId) {
              nextSequentialId = num + 1;
            }
          }
        });
      }

      // Apply the mask to generate the new ID
      if (idMasks.participantId) {
        const fullMaskRegex = maskToRegex(idMasks.participantId, $selectedSite.siteId);
        let newId = idMasks.participantId;

        // Replace {siteId} with the actual site ID
        newId = newId.replace(/{siteId}/g, $selectedSite.siteId);

        // Replace {###} pattern with sequential number
        // Count the number of # characters to determine padding
        const hashMatch = newId.match(/{#+}/);
        if (hashMatch) {
          const hashPattern = hashMatch[0];
          const paddingLength = hashPattern.length - 2; // Subtract 2 for the { and }
          const paddedNumber = nextSequentialId.toString().padStart(paddingLength, '0');
          newId = newId.replace(hashPattern, paddedNumber);
        }

        // Final check before setting the ID
        if (formMode === 'edit' || editPerson) {
          console.log('Aborting ID generation: switched to edit mode before setting ID');
          return;
        }
        
        personData.update((data) => ({
          ...data,
          subjectId: newId,
        }));
        
        // Mark that we've generated an ID for this session
        // Ensure final value matches mask before locking generation
        if (!fullMaskRegex || new RegExp(fullMaskRegex).test(newId)) {
          hasGeneratedIdThisSession = true;
        }
      }
    } catch (error) {
      console.error('Error generating subject ID:', error);
    }
  }

  async function isSubjectIdUnique(subjectId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/persons?studyGUID=${$selectedStudy._id}&subjectId=${subjectId}`
      );
      const data = await response.json();

      // If no matches found, the ID is unique
      if (data.data.length === 0) {
        return true;
      }

      // If editing, check if the only match is our current record
      if (editPerson) {
        return data.data.length === 1 && data.data[0]._id === editPerson._id;
      }

      // Otherwise, the ID is not unique
      return false;
    } catch (error) {
      console.error('Error checking subject ID uniqueness:', error);
      return false;
    }
  }

  async function validateForm() {
    let isValid = true;

    if (!$personData.password) {
      passwordError = 'Access PIN is required';
      isValid = false;
    } else if ($personData.password.length < 4) {
      passwordError = 'Access PIN must be at least 4 characters';
      isValid = false;
    } else {
      passwordError = '';
    }

    if (!$personData.subjectId) {
      subjectIdError = 'Subject ID is required';
      isValid = false;
    } else if (subjectIdPattern && !new RegExp(subjectIdPattern).test($personData.subjectId)) {
      subjectIdError = `Subject ID must match pattern ${idMasks.participantId}`;
      isValid = false;
    } else {
      // Check if subject ID is unique
      const isUnique = await isSubjectIdUnique($personData.subjectId);
      if (!isUnique) {
        subjectIdError = 'This Subject ID is already in use';
        isValid = false;
      } else {
        subjectIdError = '';
      }
    }

    if (!$personData.timezoneGUID) {
      timezoneError = 'Timezone is required';
      isValid = false;
    } else {
      timezoneError = '';
    }

    if (!$personData.languageGUID) {
      languageError = 'Language is required';
      isValid = false;
    } else {
      languageError = '';
    }

    // Check if status is being changed and require a reason and date
    if (
      editPerson &&
      originalStatusGUID &&
      $personData.statusGUID !== originalStatusGUID
    ) {
      if (!$personData.statusChangeReason || $personData.statusChangeReason.trim() === '') {
        statusChangeReasonError = 'Status change reason is required when updating participant status';
        isValid = false;
      } else {
        statusChangeReasonError = '';
      }
      
      if (!$personData.statusChangeDate) {
        statusChangeDateError = 'Status change date is required when updating participant status';
        isValid = false;
      } else {
        statusChangeDateError = '';
      }
    } else {
      statusChangeReasonError = '';
      statusChangeDateError = '';
    }

    // Validate randomization date when feature is enabled
    if (isFeatureEnabled('randomization.enabled')) {
      if (!$personData.randomizationDate) {
        randomizationDateError = 'Randomization date is required when randomization feature is enabled';
        isValid = false;
      } else {
        randomizationDateError = '';
      }
    } else {
      randomizationDateError = '';
    }

    return isValid;
  }

  onMount(async () => {
    try {
      // Fetch all necessary options 
      const [statusRes, timezoneRes, siteRes, languageRes] = await Promise.all([
        fetch(`${API_BASE_URL}/lookups/SUBSTAT`),
        fetch(`${API_BASE_URL}/lookups/TIMEZONE`),
        fetch(`${API_BASE_URL}/sites`),
        fetch(`${API_BASE_URL}/languages?page=1&pageSize=1000`),
      ]);

      const [statusData, timezoneData, siteData, languageData] = await Promise.all([
        statusRes.json(),
        timezoneRes.json(),
        siteRes.json(),
        languageRes.json(),
      ]);

      statusOptions = statusData.data;
      timezoneOptions = timezoneData.data;
      siteOptions = siteData.data;
      languageOptions = languageData.data;

      // Fetch study-specific data for both new and edit operations
      if ($selectedStudy && $selectedStudy._id) {
        await Promise.all([fetchStudyProperties(), fetchStudyLanguages()]);
      }

      // Set the default status for new persons
      if (!editPerson) {
        personData.update((data) => ({
          ...data,
          statusGUID: getDefaultStatusGUID(),
          languageGUID: getEnglishLanguageGUID(),
        }));
        // Generate PIN for new persons
        generatePIN();
        // Set default timezone after options are loaded
        const siteTimezone = timezoneOptions.find((tz) => tz.code === $selectedSite?.timezoneCode);
        if (siteTimezone) {
          $personData.timezoneGUID = siteTimezone.id;
        }
        
        // Set randomization date if feature is enabled and we have enrollment date
        if (isFeatureEnabled('randomization.enabled') && $personData.enrollmentDate) {
          const calculatedDate = calculateRandomizationDate();
          if (calculatedDate) {
            $personData.randomizationDate = calculatedDate;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  });

  // Set form mode based on whether we're editing or creating
  $: if (showForm && !editPerson) {
    formMode = 'create';
    hasGeneratedIdThisSession = false; // Reset for new participant
  }

  $: if (editPerson && showForm) {
    isSettingUp = true;
    formMode = 'edit'; // Explicitly set to edit mode
    hasGeneratedIdThisSession = true; // Don't generate ID for existing persons
    originalStatusGUID = editPerson.statusGUID?.toString() || '';
    lastStatusGUID = editPerson.statusGUID?.toString() || '';
    const formattedPerson = { ...editPerson };
    
    // Convert ObjectIds to strings for form fields
    formattedPerson.timezoneGUID = asIdString(editPerson.timezoneGUID) || '';
    formattedPerson.languageGUID = asIdString(editPerson.languageGUID) || '';
    formattedPerson.statusGUID = asIdString(editPerson.statusGUID) || '';
    
    // Format dates for form display (YYYY-MM-DD)
    formattedPerson.enrollmentDate = formatDateForInput(editPerson.enrollmentDate);
    formattedPerson.randomizationDate = formatDateForInput(editPerson.randomizationDate);
    formattedPerson.statusChangeDate = formatDateForInput(editPerson.statusChangeDate);
    
    personData.set(formattedPerson);
    hasChanges = false;
    
    // Store original data after a delay to ensure all reactive statements have run
    setTimeout(() => {
      // Use the current personData value after reactive statements have run
      const currentData = get(personData);
      originalPersonData = JSON.parse(JSON.stringify(currentData));
      isSettingUp = false;
    }, 0);
  }

  // Fallback: if editing and GUIDs don't match options, map by codes
  $: if (editPerson && showForm && !isSettingUp && timezoneOptions.length > 0) {
    const currentTzId = $personData?.timezoneGUID;
    const hasExactTz = currentTzId && timezoneOptions.some((tz) => tz.id === currentTzId);
    if (!hasExactTz && editPerson?.timezoneCode) {
      const byCode = timezoneOptions.find((tz) => tz.code === editPerson.timezoneCode);
      if (byCode) {
        $personData.timezoneGUID = byCode.id;
      }
    }
  }

  $: if (editPerson && showForm && !isSettingUp && languageOptions.length > 0) {
    const currentLangId = $personData?.languageGUID;
    const hasExactLang = currentLangId && languageOptions.some((lang) => lang._id === currentLangId);
    if (!hasExactLang && (editPerson?.languageCode || editPerson?.language)) {
      let byCode = null;
      if (editPerson.languageCode) {
        byCode = languageOptions.find((lang) => lang.code === editPerson.languageCode);
      }
      // As a last resort, match by language name if provided
      if (!byCode && editPerson.language) {
        byCode = languageOptions.find((lang) => lang.language === editPerson.language);
      }
      if (byCode) {
        $personData.languageGUID = byCode._id;
      }
    }
  }

  $: if (showForm && !editPerson) {
    isSettingUp = true;
    resetPersonData();
    originalPersonData = null;
    hasChanges = false;
    // Fetch study properties and languages after resetting person data
    if ($selectedStudy && $selectedStudy._id) {
      Promise.all([fetchStudyProperties(), fetchStudyLanguages()]);
    }
    // Use setTimeout to ensure resetPersonData and its side effects complete
    setTimeout(() => {
      isSettingUp = false;
    }, 0);
  }

  // Detect changes by comparing current data with original data
  $: if ($personData && originalPersonData && !isSettingUp) {
    // For editing existing persons, compare with original data
    const currentData = JSON.parse(JSON.stringify($personData));
    const originalData = JSON.parse(JSON.stringify(originalPersonData));
    
    hasChanges = JSON.stringify(currentData) !== JSON.stringify(originalData);
  }

  // For new persons, check if any data has been entered (only when not setting up)
  $: if ($personData && !originalPersonData && showForm && !isSettingUp) {
    const hasData = $personData.subjectId?.trim() || 
                   $personData.externalId?.trim() || 
                   $personData.timezoneGUID || 
                   $personData.languageGUID ||
                   $personData.statusChangeReason?.trim() ||
                   $personData.statusChangeDate ||
                   $personData.notes?.trim() ||
                   $personData.password?.trim() ||
                   $personData.enrollmentDate ||
                   $personData.randomizationDate ||
                   Object.keys($personData.properties || {}).length > 0;
    
    hasChanges = !!hasData;
  }

  // React to changes in idMasks to ensure ID generation happens when masks are available
  $: if (
    showForm &&
    formMode === 'create' &&
    idMasks.participantId &&
    shouldAutoGenerateSubjectId() &&
    $selectedSite &&
    $selectedSite.siteId
  ) {
    generateSubjectId();
  }

  // Auto-calculate randomization date when enrollment date changes (only for new persons)
  $: if (
    showForm &&
    !editPerson &&
    $personData.enrollmentDate &&
    isFeatureEnabled('randomization.enabled') &&
    !isSettingUp
  ) {
    const calculatedDate = calculateRandomizationDate();
    if (calculatedDate && !$personData.randomizationDate) {
      $personData.randomizationDate = calculatedDate;
    }
  }

  // Additional reactive statement to ensure randomization date is set when study properties load
  $: if (
    showForm &&
    !editPerson &&
    $personData.enrollmentDate &&
    isFeatureEnabled('randomization.enabled') &&
    !isSettingUp &&
    !$personData.randomizationDate &&
    $studyPropertiesStore?.features?.randomization
  ) {
    const calculatedDate = calculateRandomizationDate();
    if (calculatedDate) {
      $personData.randomizationDate = calculatedDate;
    }
  }

  // Ensure randomization object exists in study properties
  $: if ($studyPropertiesStore?.features && !$studyPropertiesStore.features.randomization) {
    $studyPropertiesStore.features.randomization = {
      enabled: false,
      daysAfterEnrollment: 30,
    };
  }

  // Ensure defaults are set when study properties store updates
  $: if ($studyPropertiesStore && showForm && !editPerson && !isSettingUp) {
    ensureDefaultsSet();
  }

  // Enhanced reactive statement for timezone with better timing handling
  $: if (showForm && !editPerson && !isSettingUp) {
    // Set timezone if not already set and we have the required data
    if (!$personData.timezoneGUID && timezoneOptions.length > 0 && $selectedSite?.timezoneCode) {
      const siteTimezone = timezoneOptions.find((tz) => tz.code === $selectedSite.timezoneCode);
      if (siteTimezone) {
        $personData.timezoneGUID = siteTimezone.id;
      }
    }
  }

  // Enhanced reactive statement for language with better timing handling
  $: if (showForm && !editPerson && !isSettingUp) {
    // Set language if not already set and we have the required data
    if (!$personData.languageGUID) {
      // Try study languages first if available
      if (studyLanguageGUIDs.length > 0 && studyLanguages.length > 0) {
        const defaultStudyLanguage = studyLanguages.find((sl) => sl.languageDefault);
        if (defaultStudyLanguage) {
          $personData.languageGUID = defaultStudyLanguage.languageGUID;
        } else {
          // Fallback to English if no default is set
          const englishStudyLanguage = studyLanguages.find((sl) => sl.code === 'en_US');
          if (englishStudyLanguage) {
            $personData.languageGUID = englishStudyLanguage.languageGUID;
          }
        }
      } else if (languageOptions.length > 0) {
        // Fallback to English from all languages if study languages not loaded yet
        const englishLanguage = languageOptions.find((lang) => lang.code === 'en_US');
        if (englishLanguage) {
          $personData.languageGUID = englishLanguage._id;
        }
      }
    }
  }

  // Track when user changes the status
  $: if (editPerson && originalStatusGUID && $personData.statusGUID !== originalStatusGUID && !isSettingUp) {
    // Only clear fields once when status first changes
    if (lastStatusGUID !== $personData.statusGUID) {
      $personData.statusChangeReason = '';
      $personData.statusChangeDate = '';
      lastStatusGUID = $personData.statusGUID;
    }
  }

  function resetPersonData() {
    clearValidationErrors();
    personData.set({
      _id: null,
      studyGUID: $selectedStudy._id,
      subjectId: '',
      siteGUID: $selectedSite._id,
      externalId: '',
      timezoneGUID: '',
      source: 'SUMMA',
      active: true,
      statusGUID: getDefaultStatusGUID(),
      statusDate: new Date().toISOString().split('T')[0],
      statusChangeReason: '',
      statusChangeDate: '',
      notes: '',
      properties: {},
      subjectTemplateGUID: '',
      password: '',
      enrollmentDate: new Date().toISOString().split('T')[0],
      randomizationDate: '',
      blinded: true,
      languageGUID: getEnglishLanguageGUID(),
    });
    // Generate PIN after resetting the form
    generatePIN();
    // Initialize default properties after resetting
    initializeDefaultProperties();
  }

  function close() {
    // Reset change detection when closing
    clearValidationErrors();
    originalPersonData = null;
    hasChanges = false;
    isSettingUp = false;
    dispatch('close');
  }

  // Update handleSubmit to handle async validation
  async function handleSubmit() {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Get the current person data
    const currentData = get(personData);

    // Create a new properties object starting with existing properties
    const updatedProperties = {};

    // Add all study properties
    studyProperties.forEach((prop) => {
      const key = prop.key;
      let value = currentData.properties[key];

      // Handle date/datetime values
      if (value && (prop.type === 'date' || prop.type === 'datetime')) {
        try {
          const date = new Date(value);
          if (prop.type === 'date') {
            value = date.toISOString().split('T')[0];
          } else {
            // For datetime, store the full ISO string
            value = date.toISOString();
          }
        } catch (e) {
          console.error('Error formatting date:', e);
        }
      }

      // For number type properties, ensure we convert empty strings to null
      if (prop.type === 'number') {
        updatedProperties[key] = value === '' ? null : Number(value);
      } else {
        updatedProperties[key] = value || null;
      }
    });

    // Add device type and kit number if they exist
    if (currentData.properties.enableddevice) {
      updatedProperties.enableddevice = currentData.properties.enableddevice;
    }
    if (currentData.properties.participantkitnumber) {
      updatedProperties.participantkitnumber = currentData.properties.participantkitnumber;
    }

    // Update the person data with the new properties
    currentData.properties = updatedProperties;

    // Convert enrollment date to ISO string
    if (currentData.enrollmentDate) {
      const [year, month, day] = currentData.enrollmentDate.split('-');
      currentData.enrollmentDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
    }

    // Convert randomization date to ISO string
    if (currentData.randomizationDate) {
      const [year, month, day] = currentData.randomizationDate.split('-');
      currentData.randomizationDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
    }

    // Convert status change date to ISO string if it exists
    if (currentData.statusChangeDate) {
      const [year, month, day] = currentData.statusChangeDate.split('-');
      currentData.statusChangeDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
    }

    // Dispatch the submit event with the updated data
    dispatch('submit', currentData);
    close();
  }

  function getTimezoneOffset(timezone) {
    try {
      const date = new Date();
      const options = { timeZone: timezone, timeZoneName: 'short' };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
      return timeZonePart ? ` (${timeZonePart.value})` : '';
    } catch (error) {
      console.error('Error getting timezone offset:', error);
      return '';
    }
  }

  // Add PIN generation function
  function generatePIN() {
    // Generate a random 6-digit number
    const pin = Math.floor(100000 + Math.random() * 900000).toString();
    // For new participants, always generate PIN. For existing participants, only if not setting up
    if (!editPerson) {
      $personData.password = pin;
    } else if (!isSettingUp) {
      $personData.password = pin;
    }
  }

  // Calculate randomization date based on enrollment date and configured days
  function calculateRandomizationDate() {
    if ($personData.enrollmentDate && isFeatureEnabled('randomization.enabled')) {
      const enrollmentDate = new Date($personData.enrollmentDate);
      // Use nullish coalescing so 0 is preserved (0 means manual entry required)
      const daysAfterEnrollment = $studyPropertiesStore?.features?.randomization?.daysAfterEnrollment ?? 30;
      // When 0, do not auto-calculate; require manual entry
      if (daysAfterEnrollment === 0) {
        return '';
      }
      const randomizationDate = new Date(enrollmentDate);
      randomizationDate.setDate(enrollmentDate.getDate() + daysAfterEnrollment);
      return randomizationDate.toISOString().split('T')[0];
    }
    return '';
  }

  // Add this helper function to organize timezones
  function organizeTimezones(timezones) {
    const regions = {
      'United States': [],
      Canada: [],
      'America (Other)': [],
      Europe: [],
      Asia: [],
      Africa: [],
      Pacific: [],
      Australia: [],
      Atlantic: [],
      Indian: [],
      Other: [],
    };

    timezones.forEach((tz) => {
      const [region, country, ...rest] = tz.code.split('/');

      // Special case for US territories in Pacific region
      if (tz.code === 'Pacific/Honolulu') {
        regions['United States'].push(tz);
        return;
      }

      if (region === 'America') {
        // Check for US cities and regions
        if (
          tz.code.startsWith('America/New_York') ||
          tz.code.startsWith('America/Chicago') ||
          tz.code.startsWith('America/Denver') ||
          tz.code.startsWith('America/Los_Angeles') ||
          tz.code.startsWith('America/Phoenix') ||
          tz.code.startsWith('America/Anchorage') ||
          tz.code.startsWith('America/Adak') ||
          tz.code.startsWith('America/Boise') ||
          tz.code.startsWith('America/Detroit') ||
          tz.code.includes('Indiana') ||
          tz.code.includes('Kentucky') ||
          tz.code.includes('North_Dakota') ||
          tz.code.includes('Michigan') ||
          tz.code.startsWith('America/Juneau') ||
          tz.code.startsWith('America/Sitka') ||
          tz.code.startsWith('America/Metlakatla') ||
          tz.code.startsWith('America/Yakutat') ||
          tz.code.startsWith('America/Nome') ||
          tz.code.startsWith('America/Menominee')
        ) {
          regions['United States'].push(tz);
        } else if (
          tz.code.startsWith('America/Vancouver') ||
          tz.code.startsWith('America/Edmonton') ||
          tz.code.startsWith('America/Winnipeg') ||
          tz.code.startsWith('America/Toronto') ||
          tz.code.startsWith('America/Halifax') ||
          tz.code.startsWith('America/St_Johns')
        ) {
          regions['Canada'].push(tz);
        } else {
          regions['America (Other)'].push(tz);
        }
      } else if (regions[region]) {
        regions[region].push(tz);
      } else {
        regions['Other'].push(tz);
      }
    });

    // Sort timezones within each region
    Object.keys(regions).forEach((region) => {
      regions[region].sort((a, b) => {
        // Custom sort for US timezones to follow geographical order
        if (region === 'United States') {
          const order = {
            'America/New_York': 1,
            'America/Detroit': 2,
            'America/Indiana': 3,
            'America/Kentucky': 4,
            'America/Chicago': 5,
            'America/Menominee': 6,
            'America/North_Dakota': 7,
            'America/Denver': 8,
            'America/Boise': 9,
            'America/Phoenix': 10,
            'America/Los_Angeles': 11,
            'America/Juneau': 12,
            'America/Sitka': 13,
            'America/Metlakatla': 14,
            'America/Yakutat': 15,
            'America/Anchorage': 16,
            'America/Nome': 17,
            'America/Adak': 18,
            'Pacific/Honolulu': 19,
          };
          // Sort by timezone first
          const aBase = Object.keys(order).find((key) => a.code.startsWith(key)) || a.code;
          const bBase = Object.keys(order).find((key) => b.code.startsWith(key)) || b.code;
          const orderDiff = (order[aBase] || 99) - (order[bBase] || 99);
          if (orderDiff !== 0) return orderDiff;
          // If same timezone base, sort alphabetically
          return a.code.localeCompare(b.code);
        }
        // Default alphabetical sort for other regions
        return a.code.localeCompare(b.code);
      });
    });

    // Remove empty regions
    return Object.fromEntries(Object.entries(regions).filter(([_, zones]) => zones.length > 0));
  }

  // Add this function to convert the mask to a regex pattern
  function maskToRegex(mask, siteId) {
    // Replace {siteId} with the actual siteId, escaping regex chars
    let regex = mask.replace(/{siteId}/g, siteId ? siteId.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') : '');
    // Replace {###} or similar with \\d{n}
    regex = regex.replace(/{(#+)}/g, (_, hashes) => `\\d{${hashes.length}}`);
    // Anchor the regex
    return `^${regex}$`;
  }

  // Reactive variable for the pattern
  $: subjectIdPattern = idMasks.participantId && $selectedSite?.siteId
    ? maskToRegex(idMasks.participantId, $selectedSite.siteId)
    : '';
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-4xl relative z-60"
      on:click|stopPropagation
    >
      <header class="flex justify-between items-start mb-4">
        <div class="flex items-center gap-2">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {editPerson ? 'Edit Participant' : 'New Participant'}
          </h2>
          {#if editPerson && hasChanges}
            <span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full dark:bg-yellow-900 dark:text-yellow-200">
              Modified
            </span>
          {/if}
        </div>
        <button
          on:click={close}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>
      <form on:submit|preventDefault={handleSubmit} class="space-y-4">
        <div class="flex gap-3">
          <div class="w-40">
            <label for="subjectId" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Subject ID *
            </label>
            <div class="relative">
              <input
                type="text"
                id="subjectId"
                bind:value={$personData.subjectId}
                on:input={clearSubjectIdError}
                pattern={subjectIdPattern}
                class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {!hasAdminRole() &&
                editPerson
                  ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                  : ''}"
                disabled={!hasAdminRole() && editPerson}
                required
                placeholder={$personData.subjectId
                  ? ''
                  : !shouldAutoGenerateSubjectId() || hasGeneratedIdThisSession
                    ? 'Enter ID'
                    : 'Generating...'}
              />
              {#if !editPerson && shouldShowGenerateSubjectIdButton()}
                <div class="absolute right-2 top-1">
                  <button
                    type="button"
                    class="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    on:click={() => {
                      clearSubjectIdError();
                      hasGeneratedIdThisSession = false;
                      generateSubjectId();
                    }}
                    title="Generate new ID using pattern"
                  >
                    Generate
                  </button>
                </div>
              {/if}
            </div>

            {#if idMasks.participantId}
              <p class="text-xs mt-1 {($personData.subjectId && subjectIdPattern && !$personData.subjectId.match(new RegExp(subjectIdPattern))) ? 'text-red-500' : 'text-gray-500'}">
                Pattern: {idMasks.participantId}
              </p>
            {/if}
            {#if subjectIdError}
              <p class="text-xs mt-1 text-red-500">{subjectIdError}</p>
            {/if}
          </div>
          <div class="w-44">
            <label for="status" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Status
            </label>
            <select
              id="status"
              bind:value={$personData.statusGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              required
            >
              {#each statusOptions as status}
                <option value={status.id}>{status.label}</option>
              {/each}
            </select>
          </div>
          {#if editPerson && originalStatusGUID && $personData.statusGUID !== originalStatusGUID}
            <div>
              <label
                for="statusChangeReason"
                class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >
                Status Change Reason *
              </label>
              <input
                type="text"
                id="statusChangeReason"
                bind:value={$personData.statusChangeReason}
                class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {statusChangeReasonError
                  ? 'border-red-500'
                  : ''}"
                placeholder="Enter reason"
                required
              />
              {#if statusChangeReasonError}
                <p class="text-red-500 text-xs mt-1">{statusChangeReasonError}</p>
              {/if}
            </div>
            <div>
              <label
                for="statusChangeDate"
                class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >
                Status Change Date *
              </label>
              <input
                type="date"
                id="statusChangeDate"
                bind:value={$personData.statusChangeDate}
                class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {statusChangeDateError
                  ? 'border-red-500'
                  : ''}"
                required
              />
              {#if statusChangeDateError}
                <p class="text-red-500 text-xs mt-1">{statusChangeDateError}</p>
              {/if}
            </div>
          {/if}
          <div class="w-36">
            <label for="accessPIN" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              PIN *
            </label>
            <div class="relative">
              <input
                type="text"
                id="accessPIN"
                bind:value={$personData.password}
                class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {passwordError
                  ? 'border-red-500'
                  : ''}"
                minlength="4"
                required
              />
              <div class="absolute right-2 top-1">
                <button
                  type="button"
                  class="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  on:click={generatePIN}
                  title="Generate new PIN"
                >
                  Generate
                </button>
              </div>
            </div>
            {#if passwordError}
              <p class="text-red-500 text-xs mt-1">{passwordError}</p>
            {/if}
          </div>
        </div>
        <!-- {#if isFeatureEnabled('performanceMetrics')} -->
        {#if isFeatureEnabled('randomization.enabled')}
          <div class="flex gap-3">
            <div class="w-1/3">
              <label for="enrollmentDate" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                CGM Start Date *
              </label>
              <input
                type="date"
                id="enrollmentDate"
                bind:value={$personData.enrollmentDate}
                class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div class="w-1/3">
              <label for="randomizationDate" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                Randomisation Date *
              </label>
              <div class="relative">
                <input
                  type="date"
                  id="randomizationDate"
                  bind:value={$personData.randomizationDate}
                  class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {randomizationDateError ? 'border-red-500' : ''}"
                  required
                />
                <div class="absolute right-8 top-1">
                  <button
                    type="button"
                    class="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    on:click={() => {
                      if ($personData.enrollmentDate) {
                        const date = calculateRandomizationDate();
                        if (date) {
                          $personData.randomizationDate = date;
                        }
                      }
                    }}
                    title="Calculate based on enrollment date"
                  >
                    Calculate
                  </button>
                </div>
              </div>
              {#if randomizationDateError}
                <p class="text-red-500 text-xs mt-1">{randomizationDateError}</p>
              {/if}
              {#if ($studyPropertiesStore?.features?.randomization?.daysAfterEnrollment ?? 30) === 0}
                <p class="text-xs text-gray-500 mt-1">Enter manually (no auto-calculation)</p>
              {:else}
                <p class="text-xs text-gray-500 mt-1">
                  Calculated as cgm start date + {($studyPropertiesStore?.features?.randomization?.daysAfterEnrollment ?? 30)} days
                </p>
              {/if}
            </div>
          </div>
        {/if}
        <div class="flex gap-3">
          <div class="w-1/2">
            <label for="timezone" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Timezone *
            </label>
            <select
              id="timezone"
              bind:value={$personData.timezoneGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {timezoneError
                ? 'border-red-500'
                : ''}"
              required
            >
              <option value="">Select a timezone</option>
              {#each Object.entries(organizeTimezones(timezoneOptions)) as [region, zones]}
                {#if zones.length > 0}
                  <optgroup label={region}>
                    {#each zones as timezone}
                      <option value={timezone.id}>
                        {timezone.code.split('/').slice(1).join('/')}{getTimezoneOffset(
                          timezone.code
                        )}
                      </option>
                    {/each}
                  </optgroup>
                {/if}
              {/each}
            </select>
            {#if timezoneError}
              <p class="text-red-500 text-xs mt-1">{timezoneError}</p>
            {/if}
          </div>
          <div class="w-1/3">
            <label for="language" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Language *
            </label>
            <select
              id="language"
              bind:value={$personData.languageGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {languageError
                ? 'border-red-500'
                : ''}"
              required
            >
              <option value="">Select a language</option>
              {#each languageOptions.filter(lang => studyLanguageGUIDs.length === 0 || studyLanguageGUIDs.includes(lang._id)) as language}
                <option value={language._id}>{language.displayBase}</option>
              {/each}
            </select>
            {#if languageError}
              <p class="text-red-500 text-xs mt-1">{languageError}</p>
            {/if}
          </div>
          <div class="w-20">
            <label for="externalId" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              External ID
            </label>
            <input
              type="text"
              id="externalId"
              bind:value={$personData.externalId}
              class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div class="w-20">
            <label for="active" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Active
            </label>
            <select
              id="active"
              bind:value={$personData.active}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {!hasAdminRole()
                ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                : ''}"
              disabled={!hasAdminRole()}
            >
              <option value={true}>Yes</option>
              <option value={false}>No</option>
            </select>
          </div>
          <div class="w-20">
            <label for="blinded" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Blinded
            </label>
            <select
              id="blinded"
              bind:value={$personData.blinded}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {!hasAdminRole()
                ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                : ''}"
              disabled={!hasAdminRole()}
            >
              <option value={true}>Yes</option>
              <option value={false}>No</option>
            </select>
          </div>
        </div>

        {#if studyProperties.filter((p) => p.active && !p.hidden).length > 0}
          <div class="flex gap-2 pt-6">
            <div class="w-full">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Properties
              </h3>
              <div class="grid grid-cols-3 gap-3 items-end">
                {#each studyProperties.filter((p) => p.active && !p.hidden) as property}
                  <div class="flex flex-col">
                    <label
                      for={property.key}
                      class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
                    >
                      {property.label}{property.required ? ' *' : ''}
                    </label>
                    {#if property.type === 'select'}
                      <select
                        id={property.key}
                        bind:value={$personData.properties[property.key]}
                        class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        required={property.required}
                      >
                        <option value="">Select {property.label}</option>
                        {#if property.options}
                          {#each property.options as opt}
                            <option value={opt.code}>{opt.label}</option>
                          {/each}
                        {/if}
                      </select>
                    {:else if property.type === 'number'}
                      <input
                        type="number"
                        id={property.key}
                        bind:value={$personData.properties[property.key]}
                        class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        required={property.required}
                        min={property.validation?.min}
                        max={property.validation?.max}
                      />
                    {:else if property.type === 'boolean'}
                      <select
                        id={property.key}
                        bind:value={$personData.properties[property.key]}
                        class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        required={property.required}
                      >
                        <option value="">Select {property.label}</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    {:else if property.type === 'date'}
                      <input
                        type="date"
                        id={property.key}
                        bind:value={$personData.properties[property.key]}
                        class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        required={property.required}
                      />
                    {:else if property.type === 'datetime'}
                      <input
                        type="datetime-local"
                        id={property.key}
                        bind:value={$personData.properties[property.key]}
                        class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        required={property.required}
                      />
                    {:else}
                      <input
                        type="text"
                        id={property.key}
                        bind:value={$personData.properties[property.key]}
                        class="border h-8 p-1 w-full rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        required={property.required}
                        minlength={property.validation?.minLength}
                        maxlength={property.validation?.maxLength}
                        pattern={property.validation?.pattern}
                      />
                    {/if}
                    {#if property.description}
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {property.description}
                      </p>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          </div>
        {/if}

        <div class="flex gap-3 pt-6">
          <div class="w-full">
            <label for="notes" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Notes
            </label>
            <textarea
              id="notes"
              bind:value={$personData.notes}
              class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              rows="3"
            />
          </div>
        </div>

        <div class="flex justify-end space-x-2 pt-10">
          <button
            type="button"
            on:click={close}
            class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="py-1 px-2 rounded {isReadOnly() || !hasChanges ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-700'}"
            disabled={isReadOnly() || !hasChanges}
          >
            {hasChanges ? 'Save' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
